type WebkitMessageHandlers = Record<string, { postMessage: (payload: unknown) => unknown }>;

let cachedHandlers: WebkitMessageHandlers | null | undefined;

const getWebkitMessageHandlers = (): WebkitMessageHandlers | null => {
  if (cachedHandlers) return cachedHandlers;
  if (typeof window === 'undefined') return null;
  const webkit = (window as typeof window & { webkit?: { messageHandlers?: WebkitMessageHandlers } }).webkit;
  const handlers = webkit?.messageHandlers ?? null;
  if (handlers) cachedHandlers = handlers;
  return handlers;
};

export const openDeeplink = (path: string): boolean =>
  postWebkitMessage('openDeeplink', path);

/**
 * Signals to the native host that the web app is fully rendered and
 * animation-ready. Native uses this to fade the webview in (instead of
 * relying on `load`, which fires before fonts/images/animations are ready).
 */
export const notifyReady = (): boolean =>
  postWebkitMessage('ready', {});

export const dismiss = (): boolean =>
  postWebkitMessage('dismiss', {});

export const hasWebkitMessageHandler = (handler: string): boolean =>
  Boolean(getWebkitMessageHandlers()?.[handler]);

const resolveHandler = (handler: string) =>
  getWebkitMessageHandlers()?.[handler] ?? null;

export const postWebkitMessage = (handler: string, payload: unknown): boolean => {
  const target = resolveHandler(handler);
  if (!target) return false;
  try {
    target.postMessage(payload);
    return true;
  } catch (error) {
    console.warn(`Failed to post message to ${handler}`, error);
    return false;
  }
};

export const postWebkitMessageWithReply = async <T = unknown>(
  handler: string,
  payload: unknown,
): Promise<T | null> => {
  const target = resolveHandler(handler);
  if (!target) return null;
  try {
    return (await target.postMessage(payload)) as T;
  } catch (error) {
    console.warn(`Failed to post message to ${handler}`, error);
    return null;
  }
};
